import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { enregistrerCategorie, supprimerCategorie } from '../../db/repo'
import type { Categorie, Id, Sens } from '../../domain/types'
import { Bascule, Carte, Champ, Confirmation, Encart, Entete, IconeChevron, IconePlus, Pastille, Puce, useAction } from '../composants'
import { useDonnees } from '../donnees'

export function PageCategories() {
  const { d, ix } = useDonnees()
  const navigate = useNavigate()
  const [sens, setSens] = useState<Sens>('depense')
  const racines = d.categories.filter((c) => c.sens === sens && !c.parentId)

  const item = (c: Categorie, enfant = false) => (
    <li key={c.id}>
      <Link to={`/categories/${c.id}`} className="item" style={enfant ? { marginLeft: 22 } : undefined}>
        <Pastille couleur={c.couleur} />
        <span className="grandir">{c.nom}</span>
        {c.modeleParDefautId && <Puce ton="info">{ix.modeleParId.get(c.modeleParDefautId)?.nom}</Puce>}
        {c.archive === 1 && <Puce>archivée</Puce>}
        <span className="chevron"><IconeChevron /></span>
      </Link>
    </li>
  )

  return (
    <>
      <Entete titre="Catégories" retour />
      <main className="contenu">
        <Bascule etiquette="Sens" valeur={sens} onChange={setSens} options={[{ valeur: 'depense', libelle: 'Dépenses' }, { valeur: 'revenu', libelle: 'Revenus' }]} />
        <ul className="liste">
          {racines.flatMap((r) => [item(r), ...d.categories.filter((c) => c.parentId === r.id).map((c) => item(c, true))])}
        </ul>
      </main>
      <button type="button" className="fab" aria-label="Nouvelle catégorie" onClick={() => navigate(`/categories/nouvelle?sens=${sens}`)}>
        <IconePlus />
      </button>
    </>
  )
}

export function PageCategorie() {
  const { id } = useParams()
  const { d } = useDonnees()
  const navigate = useNavigate()
  const existante = d.categories.find((c) => c.id === id)
  const [params] = useSearchParams()
  const sensInitial: Sens = params.get('sens') === 'revenu' ? 'revenu' : 'depense'

  const [nom, setNom] = useState(existante?.nom ?? '')
  const [sens, setSens] = useState<Sens>(existante?.sens ?? sensInitial)
  const [parentId, setParentId] = useState<Id | null>(existante?.parentId ?? null)
  const [couleur, setCouleur] = useState(existante?.couleur ?? '#8e6cef')
  const [modeleId, setModeleId] = useState<Id | null>(existante?.modeleParDefautId ?? null)
  const [archive, setArchive] = useState<0 | 1>(existante?.archive ?? 0)
  const [confirmer, setConfirmer] = useState(false)
  const { erreur, executer, setErreur } = useAction()

  const aDesEnfants = existante ? d.categories.some((c) => c.parentId === existante.id) : false
  const parents = d.categories.filter((c) => c.sens === sens && !c.parentId && c.id !== existante?.id)

  const enregistrer = () =>
    executer(async () => {
      if (!nom.trim()) throw new Error('Donnez un nom.')
      await enregistrerCategorie({
        id: existante?.id,
        nom: nom.trim(),
        sens,
        parentId: aDesEnfants ? null : parentId,
        couleur,
        archive,
        modeleParDefautId: sens === 'depense' ? modeleId : null,
      })
      navigate(-1)
    })

  return (
    <>
      <Entete titre={existante ? existante.nom : 'Nouvelle catégorie'} retour />
      <main className="contenu">
        <Carte>
          <div className="pile">
            <Champ libelle="Nom">
              <input type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
            </Champ>
            {!existante && (
              <Bascule etiquette="Sens" valeur={sens} onChange={(s) => { setSens(s); setParentId(null) }} options={[{ valeur: 'depense', libelle: 'Dépense' }, { valeur: 'revenu', libelle: 'Revenu' }]} />
            )}
            {!aDesEnfants && (
              <Champ libelle="Sous-catégorie de">
                <select value={parentId ?? ''} onChange={(e) => setParentId(e.target.value || null)}>
                  <option value="">— catégorie principale —</option>
                  {parents.map((p) => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
              </Champ>
            )}
            <Champ libelle="Couleur">
              <input type="color" value={couleur} onChange={(e) => setCouleur(e.target.value)} style={{ width: 64, height: 40, padding: 2 }} />
            </Champ>
            {sens === 'depense' && (
              <Champ
                libelle="Répartition proposée par défaut"
                aide="Pré-remplit la répartition à la saisie, sans l’imposer : chaque dépense reste modifiable. Une sous-catégorie sans modèle hérite de celui de sa catégorie."
              >
                <select value={modeleId ?? ''} onChange={(e) => setModeleId(e.target.value || null)}>
                  <option value="">Aucune (à ma charge)</option>
                  {d.modeles.map((m) => (
                    <option key={m.id} value={m.id}>{m.nom}</option>
                  ))}
                </select>
              </Champ>
            )}
            {existante && (
              <label className="case">
                <input type="checkbox" checked={archive === 1} onChange={(e) => setArchive(e.target.checked ? 1 : 0)} />
                Archivée (plus proposée à la saisie)
              </label>
            )}
          </div>
        </Carte>
        {erreur && <Encart ton="danger">{erreur}</Encart>}
        <button type="button" className="btn btn--principal btn--bloc" onClick={() => void enregistrer()}>Enregistrer</button>
        {existante && <button type="button" className="btn btn--danger btn--bloc" onClick={() => setConfirmer(true)}>Supprimer</button>}
      </main>
      {confirmer && existante && (
        <Confirmation
          titre="Supprimer cette catégorie ?"
          message="Seule une catégorie inutilisée peut être supprimée. Sinon, archivez-la."
          libelleConfirmer="Supprimer"
          danger
          onAnnuler={() => setConfirmer(false)}
          onConfirmer={() => {
            setConfirmer(false)
            void supprimerCategorie(existante.id)
              .then(() => navigate('/categories', { replace: true }))
              .catch((e: unknown) => setErreur(e instanceof Error ? e.message : String(e)))
          }}
        />
      )}
    </>
  )
}
